// Generic SAB ring layout (P0.2) — the floor plan every ring in Architecture §2 shares.
//
// P0.2 owns the *shape* of a ring: where the header fields sit, how big the buffer is, which
// byte a given slot starts at. P0.3 owns the *behaviour* — Atomics, the SPSC protocol, and
// the two policies (overwrite-oldest for signals, reject-and-count for commands). Keeping
// them apart means the arithmetic that decides where bytes land is testable without threads.

import {
  asByteOffset,
  asInt32Index,
  asSequence,
  asSlotIndex,
  type ByteOffset,
  type Int32Index,
  type Sequence,
  type SlotIndex,
} from './branded';

/**
 * Cache line size on every CPU we target. The header is padded to this so the producer's
 * counter and the consumer's counter never share a line.
 *
 * Without the padding, the audio thread bumping WRITE_SEQUENCE invalidates the line holding
 * READ_SEQUENCE on the reader's core, and the two ping-pong the line between cores on every
 * 128-sample block. That is pure jitter on a thread with a 2.7 ms deadline and no slack —
 * Principle 1, "audio is sacred", is largely a series of small decisions like this one.
 */
const CACHE_LINE_BYTES = 64;

/** 'SYNR' in ASCII. A buffer that does not start with this is not one of ours. */
export const RING_MAGIC = 0x53594e52;

export const MAX_RING_VERSION = 255;

/**
 * Capacity ceiling. `sequenceDistance` relies on uint32 wraparound arithmetic, which is only
 * correct while the true distance stays under 2^32. Capping capacity at 2^30 leaves three
 * orders of magnitude of headroom over anything we would plausibly allocate.
 */
export const MAX_CAPACITY_SLOTS = 2 ** 30;

/**
 * Slots are multiples of 8 bytes so f64 fields land 8-aligned in every slot. This is not
 * tidiness: constructing a Float64Array view at a misaligned byteOffset throws at runtime.
 */
export const SLOT_ALIGNMENT_BYTES = 8;

/**
 * Header field byte offsets — three cache lines, and the split is the whole point.
 *
 *   line 0 (0..63)    static, written once at construction, read-only forever after
 *   line 1 (64..127)  producer-owned: only the writing thread mutates these
 *   line 2 (128..191) consumer-owned: only the reading thread mutates these
 *
 * Every field is 4-byte aligned because Atomics operate on Int32Array elements.
 */
export const RING_HEADER = {
  /** u32 — RING_MAGIC. */
  MAGIC: 0,
  /** u32 holding a value constrained to 1..255 — P0.2's "versioning byte". */
  VERSION: 4,
  /** u32 — capacity in slots. Always a power of two. */
  CAPACITY_SLOTS: 8,
  /** u32 — bytes per slot. */
  SLOT_BYTES: 12,

  /** u32 atomic — slots ever written. Producer line. */
  WRITE_SEQUENCE: 64,
  /** u32 atomic — commands the producer refused because the ring was full (reject-and-count). */
  PRODUCER_DROPPED: 68,

  /** u32 atomic — slots ever read. Consumer line. */
  READ_SEQUENCE: 128,
  /** u32 atomic — slots the consumer found already overwritten (overwrite-oldest). */
  CONSUMER_DROPPED: 132,
} as const;

export const RING_HEADER_BYTES = CACHE_LINE_BYTES * 3;

export interface RingLayoutSpec {
  /** Used in error messages and diagnostics only. */
  readonly name: string;
  /** 1..255. Bump when the slot record changes shape, so a stale bundle fails loudly. */
  readonly version: number;
  /** Power of two, so the modulo in slotIndexFor is a single mask instruction. */
  readonly capacitySlots: number;
  /** Positive multiple of 8. */
  readonly slotBytes: number;
}

export interface RingLayout extends RingLayoutSpec {
  readonly headerBytes: number;
  readonly payloadByteOffset: ByteOffset;
  /** `capacitySlots - 1`. Valid as a mask precisely because capacity is a power of two. */
  readonly slotIndexMask: number;
  /** What to pass to `new SharedArrayBuffer(...)`. */
  readonly totalBytes: number;
}

/**
 * Validates a spec and derives its byte layout. Called once per ring at construction, never
 * on a real-time path, so it is free to be thorough — every throw here is a bug that would
 * otherwise surface as silent memory corruption.
 */
export function computeRingLayout(spec: RingLayoutSpec): RingLayout {
  const { name, version, capacitySlots, slotBytes } = spec;

  if (!Number.isInteger(version) || version < 1 || version > MAX_RING_VERSION) {
    throw new RangeError(
      `${name} ring: version must be an integer in 1..${String(MAX_RING_VERSION)}, got ${String(version)}`,
    );
  }
  if (!Number.isInteger(capacitySlots) || capacitySlots < 1) {
    throw new RangeError(
      `${name} ring: capacitySlots must be a positive integer, got ${String(capacitySlots)}`,
    );
  }
  if ((capacitySlots & (capacitySlots - 1)) !== 0) {
    throw new RangeError(
      `${name} ring: capacitySlots must be a power of two, got ${String(capacitySlots)}`,
    );
  }
  if (capacitySlots > MAX_CAPACITY_SLOTS) {
    throw new RangeError(
      `${name} ring: capacitySlots must be <= ${String(MAX_CAPACITY_SLOTS)}, got ${String(capacitySlots)}`,
    );
  }
  if (
    !Number.isInteger(slotBytes) ||
    slotBytes < 1 ||
    slotBytes % SLOT_ALIGNMENT_BYTES !== 0
  ) {
    throw new RangeError(
      `${name} ring: slotBytes must be a positive multiple of ${String(SLOT_ALIGNMENT_BYTES)}, got ${String(slotBytes)}`,
    );
  }

  return {
    name,
    version,
    capacitySlots,
    slotBytes,
    headerBytes: RING_HEADER_BYTES,
    payloadByteOffset: asByteOffset(RING_HEADER_BYTES),
    slotIndexMask: capacitySlots - 1,
    totalBytes: RING_HEADER_BYTES + capacitySlots * slotBytes,
  };
}

/** Byte offset → Int32Array element index, which is what Atomics take. */
export function int32IndexOf(byteOffset: number): Int32Index {
  return asInt32Index(byteOffset >>> 2);
}

/** Masks a monotonic sequence down to the slot it addresses. */
export function slotIndexFor(layout: RingLayout, sequence: Sequence): SlotIndex {
  return asSlotIndex(sequence & layout.slotIndexMask);
}

/** Where a sequence's slot begins, in bytes from the start of the buffer. */
export function slotByteOffset(layout: RingLayout, sequence: Sequence): ByteOffset {
  return asByteOffset(
    layout.payloadByteOffset + slotIndexFor(layout, sequence) * layout.slotBytes,
  );
}

/** The next sequence, wrapping through uint32 exactly as the readers expect. */
export function advanceSequence(sequence: Sequence, by = 1): Sequence {
  return asSequence((sequence + by) >>> 0);
}

/**
 * How many slots `to` is ahead of `from`.
 *
 * The `>>> 0` is load-bearing, not decoration. Sequences are uint32 counters that wrap every
 * ~4.3 billion slots; once `to` has wrapped past zero and `from` has not, the plain
 * subtraction is a large negative number. Coercing to uint32 recovers the true distance, and
 * stays correct as long as the real distance is under 2^32 — which MAX_CAPACITY_SLOTS
 * guarantees by a wide margin. Get this wrong and the ring reports itself billions of slots
 * behind, once, hours into a set.
 */
export function sequenceDistance(from: Sequence, to: Sequence): number {
  return (to - from) >>> 0;
}
